import { getCurrentUser, signOut } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Amplify } from 'aws-amplify';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { apiFetch, selectOrganization, setActiveProfile } from './api';
import awsConfig from './aws-exports';
import AuthCallback from './components/AuthCallback';
import MapPage from './components/MapPage';
import ProfileSetup from './components/ProfileSetup';
import SignIn from './components/Signin';
import { authErrorMessage } from './utils/auth-error';

Amplify.configure(awsConfig);

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const request = useRef(0);
  const fetchUserData = async (currentUser = user) => {
    if (!currentUser) return;
    const version = ++request.current;
    setLoading(true);
    setError(null);
    setUserData(null);
    setActiveProfile(null);
    try {
      const response = await apiFetch('/user');
      const data = await response.json();
      if (!response.ok) throw new Error(authErrorMessage(data));
      if (version !== request.current) return;
      setOrganizations(data.organizations);
      setUserData(data.user);
      setActiveProfile(data.user);
      setNeedsProfileCompletion(Boolean(data.needs_profile_completion));
    } catch (failure) {
      if (version === request.current) setError(failure.message);
    } finally {
      if (version === request.current) setLoading(false);
    }
  };
  const checkUser = async () => {
    try {
      const current = await getCurrentUser();
      setUser(current);
      await fetchUserData(current);
    } catch {
      setUser(null);
      setUserData(null);
      setActiveProfile(null);
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    ++request.current;
    await signOut();
    selectOrganization(null);
    setUser(null);
    setUserData(null);
    setOrganizations([]);
  };
  useEffect(() => {
    checkUser();
    return Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        checkUser();
        if (window.location.pathname === '/auth/callback')
          window.history.replaceState(null, '', '/');
      }
      if (payload.event === 'signedOut' || payload.event === 'signOut') {
        ++request.current;
        selectOrganization(null);
        setUser(null);
        setUserData(null);
      }
    });
  }, []);

  if (loading)
    return (
      <Box sx={{ height: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label='Loading organization' />
      </Box>
    );
  if (!user && window.location.pathname === '/auth/callback')
    return <AuthCallback />;
  return (
    <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
      {!user ? (
        <SignIn />
      ) : error ? (
        <Box sx={{ p: 4 }}>
          <Alert severity='error'>{error}</Alert>
          <Button onClick={() => fetchUserData()}>Retry</Button>
          <Button
            onClick={() => {
              selectOrganization(null);
              fetchUserData();
            }}
          >
            Choose organization
          </Button>
          <Button onClick={handleSignOut}>Sign out</Button>
        </Box>
      ) : !userData ? (
        <Box sx={{ p: 4, maxWidth: 500, m: 'auto' }}>
          <Typography variant='h5' gutterBottom>
            Select organization
          </Typography>
          <Select
            fullWidth
            value=''
            displayEmpty
            inputProps={{ 'aria-label': 'Organization' }}
            onChange={(event) => {
              selectOrganization(event.target.value);
              fetchUserData();
            }}
          >
            <MenuItem value='' disabled>
              Choose your organization
            </MenuItem>
            {organizations.map((organization) => (
              <MenuItem key={organization.id} value={organization.id}>
                {organization.name}
              </MenuItem>
            ))}
          </Select>
          <Button onClick={handleSignOut}>Sign out</Button>
        </Box>
      ) : needsProfileCompletion ? (
        <ProfileSetup
          fetchUserData={() => fetchUserData()}
          onProfileUpdated={() => {
            setIsEditingProfile(false);
            fetchUserData();
          }}
          isEditing={isEditingProfile}
          onSignOut={handleSignOut}
          onCancel={() => {
            setIsEditingProfile(false);
            setNeedsProfileCompletion(false);
          }}
        />
      ) : (
        <MapPage
          key={`${userData.organizationId}:${userData.app_user_id}`}
          user={user}
          userData={userData}
          onEditProfile={() => {
            setIsEditingProfile(true);
            setNeedsProfileCompletion(true);
          }}
          refreshUserData={() => fetchUserData()}
          organizations={organizations}
          onOrganizationChange={(id) => {
            selectOrganization(id);
            fetchUserData();
          }}
        />
      )}
    </SnackbarProvider>
  );
}
export default App;
